import type { ChangeEvent, ComponentProps, InputHTMLAttributes } from 'react';
import React, { useEffect, useState } from 'react';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateRange, type RangeKeyDict } from 'react-date-range';
import { format, isBefore, isValid, parse } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { baseFieldClasses } from '../form/Input';
import { getBrowserTimezone } from '../../utils/get-browser-timezone';
import { startOfOmnidots } from '../../utils/date-fns';
import Tooltip from '../../utils/tooltip';
import { LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT } from '../../constants';
import {
    formatDateTimeInTimezone,
    parseAndNormalizeDateRangeInTimezone,
    parseDateTimeInTimezone,
} from '../../utils/date-range';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface DateInputProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'> {
    value: string;
    onChange: (value: string) => string;
    timezone: string;
    onValidityChange?: (valid: boolean) => void;
    popoverProps?: Pick<ComponentProps<typeof Tooltip>, 'appendTo' | 'maxWidth' | 'popperOptions'>;
    popoverClassName?: string;
}

const DISPLAY_FORMAT = 'dd/MM/yyyy, hh:mm aa';
const DISPLAY_PLACEHOLDER = 'dd/mm/yyyy, --:-- --';
const TIME_FORMAT = 'HH:mm:ss';
const RANGE_COLOR = '#F8DA28';

function parseDisplayDateTime(value: string, timezone: string): Date | null {
    const displayDate = parse(value, DISPLAY_FORMAT, new Date());
    if (isValid(displayDate) && !isBefore(displayDate, startOfOmnidots)) {
        return parseDateTimeInTimezone(
            format(displayDate, LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT),
            timezone
        );
    }

    return null;
}

function parseQueryDate(value: string, timezone: string): Date | null {
    const date = parseDateTimeInTimezone(value, timezone);
    return date ? utcToZonedTime(date, timezone) : null;
}

function copyDateWithTime(
    date: Date,
    hours: number,
    minutes: number,
    seconds = date.getSeconds(),
    milliseconds = date.getMilliseconds()
): Date {
    const nextDate = new Date(date);
    nextDate.setHours(hours, minutes, seconds, milliseconds);
    return nextDate;
}

interface DateRangeValues {
    startDate: string;
    endDate: string;
}

function getCorrectedDateRangeValues(
    startDateValue: string,
    endDateValue: string,
    timezone: string
): DateRangeValues | null {
    const startDate = parseDateTimeInTimezone(startDateValue, timezone);
    const endDate = parseDateTimeInTimezone(endDateValue, timezone);
    const dateRange = parseAndNormalizeDateRangeInTimezone(
        { startDate: startDateValue, endDate: endDateValue },
        timezone
    );

    const correctedRange = {
        startDate: dateRange.startDate
            ? formatDateTimeInTimezone(dateRange.startDate, timezone)
            : startDateValue,
        endDate: dateRange.endDate
            ? formatDateTimeInTimezone(dateRange.endDate, timezone)
            : endDateValue,
    };
    if (
        dateRange.startDate?.getTime() === startDate?.getTime() &&
        dateRange.endDate?.getTime() === endDate?.getTime()
    ) {
        return null;
    }

    return correctedRange;
}

export function DateInput({
    value,
    onChange,
    timezone,
    onValidityChange,
    popoverProps,
    popoverClassName = '',
    ...props
}: DateInputProps) {
    const [visible, setVisible] = useState(false);
    const [draftValue, setDraftValue] = useState<string | null>(null);
    const parsedValue = value ? parseQueryDate(value, timezone) : null;
    const hasValidSelectedDate = parsedValue !== null && isValid(parsedValue);
    const hasValue = Boolean(value);
    const selectedDate = hasValidSelectedDate ? parsedValue : new Date();
    const displayValue = hasValidSelectedDate ? format(selectedDate, DISPLAY_FORMAT) : '';
    const inputValue = draftValue ?? displayValue;
    const timeInputId = props.id ? `${props.id}-time` : undefined;

    useEffect(() => {
        setDraftValue((draft) => {
            if (draft === null) return null;
            const parsedDraft = parseDisplayDateTime(draft, timezone);
            const parsedDate = parseDateTimeInTimezone(value, timezone);
            return parsedDraft && parsedDate && parsedDraft.getTime() === parsedDate.getTime()
                ? draft
                : null;
        });
    }, [value, timezone]);

    function openPicker() {
        setDraftValue((previousDraft) => previousDraft ?? displayValue);
        setVisible(true);
    }

    function updateDraftValue(dateValue: string): void {
        const parsedDate = parseQueryDate(dateValue, timezone);
        setDraftValue(parsedDate ? format(parsedDate, DISPLAY_FORMAT) : null);
    }

    function changeDate(nextValue: string): void {
        const acceptedValue = onChange(nextValue);

        if (acceptedValue !== nextValue) {
            updateDraftValue(acceptedValue);
        }
    }

    function applyDate(nextDate: Date) {
        onValidityChange?.(true);
        const nextLocalDateTime = format(nextDate, LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT);
        const nextDateInTimezone = parseDateTimeInTimezone(nextLocalDateTime, timezone);
        const nextValue = nextDateInTimezone
            ? formatDateTimeInTimezone(nextDateInTimezone, timezone)
            : nextLocalDateTime;
        const acceptedValue = onChange(nextValue);
        updateDraftValue(acceptedValue);
    }

    function clearDate() {
        onValidityChange?.(true);
        setDraftValue(null);
        changeDate('');
        setVisible(false);
    }

    function handleTextInputChange(e: ChangeEvent<HTMLInputElement>) {
        const nextDraftValue = e.target.value;
        setDraftValue(nextDraftValue);
        onValidityChange?.(parseDisplayDateTime(nextDraftValue, timezone) !== null);
    }

    function handleTextInputBlur() {
        if (draftValue === null || draftValue === displayValue) return;

        if (!draftValue.trim()) {
            onValidityChange?.(true);
            changeDate('');
            return;
        }

        const parsedDate = parseDisplayDateTime(draftValue, timezone);
        if (parsedDate) {
            changeDate(formatDateTimeInTimezone(parsedDate, timezone));
        }
    }

    function handleTimeInputChange(e: ChangeEvent<HTMLInputElement>) {
        const [hours, minutes, seconds] = e.target.value.split(':').map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return;
        }

        applyDate(
            copyDateWithTime(selectedDate, hours, minutes, seconds ?? selectedDate.getSeconds())
        );
    }

    return (
        <Tooltip
            visible={visible}
            onClickOutside={() => setVisible(false)}
            interactive={true}
            placement="bottom-start"
            {...popoverProps}
            content={
                <div className={`flex flex-col gap-3 p-2 ${popoverClassName}`}>
                    <DateRange
                        minDate={startOfOmnidots}
                        onChange={({ selection }: RangeKeyDict) => {
                            if (!selection.startDate) {
                                return;
                            }

                            applyDate(
                                copyDateWithTime(
                                    selection.startDate,
                                    selectedDate.getHours(),
                                    selectedDate.getMinutes(),
                                    selectedDate.getSeconds(),
                                    selectedDate.getMilliseconds()
                                )
                            );
                        }}
                        ranges={[
                            {
                                startDate: selectedDate,
                                endDate: selectedDate,
                                key: 'selection',
                            },
                        ]}
                        focusedRange={[0, 0]}
                        preventSnapRefocus={true}
                        moveRangeOnFirstSelection={false}
                        showDateDisplay={false}
                        months={1}
                        direction="vertical"
                        rangeColors={[RANGE_COLOR]}
                    />
                    <div className="flex items-center gap-2">
                        <label htmlFor={timeInputId} className="text-xs">
                            {gettext('Time')}
                        </label>
                        <input
                            id={timeInputId}
                            type="time"
                            step={1}
                            value={format(selectedDate, TIME_FORMAT)}
                            onChange={handleTimeInputChange}
                            className={`${baseFieldClasses} px-2 py-1 text-sm`}
                            aria-label={gettext('SELECT_TIME')}
                        />
                    </div>
                </div>
            }
        >
            <div className="relative">
                <input
                    type="text"
                    value={inputValue}
                    placeholder={DISPLAY_PLACEHOLDER}
                    onChange={handleTextInputChange}
                    onBlur={handleTextInputBlur}
                    onClick={openPicker}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            openPicker();
                        }
                    }}
                    className={`${baseFieldClasses} px-3 py-2 ${hasValue ? 'pr-9' : 'pr-3'}`}
                    {...props}
                />
                {hasValue && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            clearDate();
                        }}
                        className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        aria-label={gettext('CLEAR')}
                    >
                        <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                    </button>
                )}
            </div>
        </Tooltip>
    );
}

interface DateRangePickerProps {
    startDate: string;
    endDate: string;
    onDateRangeChange: (startDate: string, endDate: string) => void;
    timezone?: string;
    isTimezoneReady?: boolean;
}

function DateRangePicker({
    startDate,
    endDate,
    onDateRangeChange,
    timezone = getBrowserTimezone(),
    isTimezoneReady = true,
}: DateRangePickerProps) {
    useEffect(() => {
        if (!isTimezoneReady) {
            return;
        }

        const correctedDateRange = getCorrectedDateRangeValues(startDate, endDate, timezone);

        if (correctedDateRange) {
            onDateRangeChange(correctedDateRange.startDate, correctedDateRange.endDate);
        }
    }, [endDate, isTimezoneReady, onDateRangeChange, startDate, timezone]);

    function handleDateChange(dateKey: keyof DateRangeValues, value: string): string {
        const dateRange: DateRangeValues = { startDate, endDate };
        dateRange[dateKey] = value;
        const correctedDateRange = isTimezoneReady
            ? getCorrectedDateRangeValues(dateRange.startDate, dateRange.endDate, timezone)
            : null;
        const acceptedDateRange = correctedDateRange ?? dateRange;

        onDateRangeChange(acceptedDateRange.startDate, acceptedDateRange.endDate);
        return acceptedDateRange[dateKey];
    }

    function handleStartDateChange(value: string): string {
        return handleDateChange('startDate', value);
    }

    function handleEndDateChange(value: string): string {
        return handleDateChange('endDate', value);
    }

    return (
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-end">
            <div className="w-full md:w-[210px]">
                <DateInput
                    value={startDate}
                    onChange={handleStartDateChange}
                    timezone={timezone}
                    aria-label={gettext('FILTER_START_DATE')}
                />
            </div>
            <span className="hidden px-1 md:inline-block"> - </span>
            <div className="w-full md:w-[210px]">
                <DateInput
                    value={endDate}
                    onChange={handleEndDateChange}
                    timezone={timezone}
                    aria-label={gettext('FILTER_END_DATE')}
                />
            </div>
        </div>
    );
}

export default DateRangePicker;
